import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import { ToothDetailsNotesComponent } from '../tooth-details-notes';
import { ToothDetailsImagingComponent } from '../tooth-details-imaging';
import { ToothHighlightComponent } from '../tooth-highlight';
import { ToothDetailsView, ToothDetailsVM } from './tooth-details.vm';

const INITIAL_LEFT_PANE_PERCENT = 40;
const INITIAL_RIGHT_PANE_PERCENT = 60;
const COLLAPSED_LEFT_PANE_WIDTH_PX = 60;
const COLLAPSED_RIGHT_PANE_WIDTH_PX = 60;
const MIN_LEFT_PANE_WIDTH_PX = 245;
const MIN_RIGHT_PANE_WIDTH_PX = 184;
const HANDLE_WIDTH_PX = 34;
@Component({
  selector: 'ffd-tooth-details',
  templateUrl: 'tooth-details.component.html',
  styleUrls: ['./tooth-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgIconComponent,
    TranslateModule,
    ToothDetailsNotesComponent,
    ToothDetailsImagingComponent,
    ToothHighlightComponent,
    DragDropModule,
  ],
})
export class ToothDetailsComponent implements OnInit, AfterViewInit {
  private readonly tagPusherService = inject(TagPusherService);

  @Input({ required: true }) public vm!: ToothDetailsVM;

  @Output() public leftClick = new EventEmitter<void>();
  @Output() public rightClick = new EventEmitter<void>();
  @Output() public closeClick = new EventEmitter<void>();
  @Output() public visitDateClick = new EventEmitter<string>();
  @Output() public hideConditionClick = new EventEmitter<number>();
  @Output() public deleteProgressNoteClick = new EventEmitter<number>();
  @Output() public toggleClick = new EventEmitter<ToothDetailsView>();

  @ViewChild(ToothDetailsNotesComponent) public toothDetailsNotesComponent!: ToothDetailsNotesComponent;
  @ViewChild('container') public containerRef!: ElementRef;
  @ViewChild('detailsContainer') public detailsContainerRef!: ElementRef;
  @ViewChild('handle') public handleRef!: ElementRef;

  private containerWidth = 0;

  public leftPaneWidthSignal = signal(0);
  public rightPaneWidthSignal = signal(0);
  public isLeftPaneCollapsed = signal(false);
  public isRightPaneCollapsed = signal(false);
  public leftPaneOpacitySignal = signal(1);
  public rightPaneOpacitySignal = signal(1);

  public ngOnInit(): void {
    this.tagPusherService.pushTag({
      event: TagEvent.ChartingToothDetailsOpened,
      patient_id: this.vm.patientId,
    });
  }

  public ngAfterViewInit(): void {
    if (this.containerRef && this.containerRef.nativeElement) {
      this.containerWidth = this.containerRef.nativeElement.getBoundingClientRect().width;
      this.initializePaneWidths(INITIAL_LEFT_PANE_PERCENT, INITIAL_RIGHT_PANE_PERCENT);
    }

    if (this.detailsContainerRef) {
      this.detailsContainerRef.nativeElement.style.setProperty('overflow', 'hidden');
    }
  }

  public onLeftClick(): void {
    this.leftClick.emit();
  }

  public onRightClick(): void {
    this.rightClick.emit();
  }

  public onToggleClick(id: ToothDetailsView): void {
    this.toggleClick.emit(id);
  }

  public onCloseClick(): void {
    this.closeClick.emit();
  }

  public onVisitDateClick(visitId: string): void {
    this.visitDateClick.emit(visitId);
  }

  public onHideConditionClick(noteId: number): void {
    this.hideConditionClick.emit(noteId);
  }

  public onDeleteProgressNoteClick(noteId: number): void {
    this.deleteProgressNoteClick.emit(noteId);
  }

  public onNotesCollapseClick(): void {
    const containerWidthPx = this.getContainerWidth();
    if (containerWidthPx === 0) return;

    const targetLeftPercent = this.isLeftPaneCollapsed()
      ? INITIAL_LEFT_PANE_PERCENT
      : this.pixelsToPercent(COLLAPSED_LEFT_PANE_WIDTH_PX, containerWidthPx);
    this.updatePaneWidths(targetLeftPercent, containerWidthPx);
    this.isLeftPaneCollapsed.update((collapsed) => !collapsed);
    this.isRightPaneCollapsed.set(false);
  }

  public onImagingCollapseClick(): void {
    const containerWidthPx = this.getContainerWidth();
    if (containerWidthPx === 0) return;

    const targetRightPercent = this.isRightPaneCollapsed()
      ? INITIAL_RIGHT_PANE_PERCENT
      : this.pixelsToPercent(COLLAPSED_RIGHT_PANE_WIDTH_PX, containerWidthPx);
    const targetRightPx = this.percentToPixels(targetRightPercent, containerWidthPx);
    const targetLeftPercent = this.pixelsToPercent(containerWidthPx - targetRightPx - HANDLE_WIDTH_PX, containerWidthPx);

    this.updatePaneWidths(targetLeftPercent, containerWidthPx);
    this.isRightPaneCollapsed.update((collapsed) => !collapsed);
    this.isLeftPaneCollapsed.set(false);
  }

  public onDragMoved(event: CdkDragMove): void {
    if (!this.containerRef || !this.containerRef.nativeElement || this.containerWidth === 0) {
      return;
    }

    const containerRect = this.containerRef.nativeElement.getBoundingClientRect();
    const containerWidthPx = containerRect.width;

    // Calculate the current X position of the pointer relative to the container's left edge
    const newLeftPaneWidthPx = event.pointerPosition.x - containerRect.left - HANDLE_WIDTH_PX / 2;

    // Apply constraints and handle collapsed states
    const { leftPaneWidthPx, leftCollapsed, rightCollapsed } = this.applyPaneConstraints(newLeftPaneWidthPx, containerWidthPx);

    // Update opacity based on pane widths
    this.updatePaneOpacity(leftPaneWidthPx, containerWidthPx, leftCollapsed, rightCollapsed);

    // Update pane widths
    const leftPercent = this.pixelsToPercent(leftPaneWidthPx, containerWidthPx);
    this.updatePaneWidths(leftPercent, containerWidthPx);

    // Reset drag position
    event.source._dragRef.reset();
  }

  private initializePaneWidths(leftPercent: number, rightPercent: number): void {
    if (this.containerWidth === 0) {
      return;
    }

    // Calculate the total available width for the two panes (excluding the handle)
    const availableWidthForPanesPx = this.containerWidth - HANDLE_WIDTH_PX;

    const initialLeftPanePx = (availableWidthForPanesPx * leftPercent) / 100;
    const initialRightPanePx = (availableWidthForPanesPx * rightPercent) / 100;

    const initialLeftPanePercent = (initialLeftPanePx / this.containerWidth) * 100;
    const initialRightPanePercent = (initialRightPanePx / this.containerWidth) * 100;

    this.leftPaneWidthSignal.set(initialLeftPanePercent);
    this.rightPaneWidthSignal.set(initialRightPanePercent);
  }

  /**
   * Gets the current container width in pixels
   */
  private getContainerWidth(): number {
    if (!this.containerRef?.nativeElement) return 0;
    return this.containerRef.nativeElement.getBoundingClientRect().width;
  }

  /**
   * Converts pixels to percentage based on container width
   */
  private pixelsToPercent(pixels: number, containerWidth: number): number {
    return (pixels / containerWidth) * 100;
  }

  /**
   * Converts percentage to pixels based on container width
   */
  private percentToPixels(percent: number, containerWidth: number): number {
    return (percent / 100) * containerWidth;
  }

  /**
   * Updates both pane width signals based on left pane percentage
   */
  private updatePaneWidths(leftPercent: number, containerWidthPx: number): void {
    const leftPx = this.percentToPixels(leftPercent, containerWidthPx);
    const rightPercent = this.pixelsToPercent(containerWidthPx - leftPx - HANDLE_WIDTH_PX, containerWidthPx);

    this.leftPaneWidthSignal.set(leftPercent);
    this.rightPaneWidthSignal.set(rightPercent);
  }

  /**
   * Applies constraints to pane widths and returns the constrained values
   */
  private applyPaneConstraints(
    requestedLeftPx: number,
    containerWidthPx: number,
  ): {
    leftPaneWidthPx: number;
    leftCollapsed: boolean;
    rightCollapsed: boolean;
  } {
    let leftPaneWidthPx = requestedLeftPx;
    let leftCollapsed = false;
    let rightCollapsed = false;

    // Apply left pane constraints
    if (leftPaneWidthPx < COLLAPSED_LEFT_PANE_WIDTH_PX) {
      leftPaneWidthPx = COLLAPSED_LEFT_PANE_WIDTH_PX;
      leftCollapsed = true;
    }

    // Apply right pane constraints
    const maxLeftPaneWidthPx = containerWidthPx - HANDLE_WIDTH_PX - COLLAPSED_RIGHT_PANE_WIDTH_PX;
    if (leftPaneWidthPx > maxLeftPaneWidthPx) {
      leftPaneWidthPx = maxLeftPaneWidthPx;
      rightCollapsed = true;
    }

    this.isLeftPaneCollapsed.set(leftCollapsed);
    this.isRightPaneCollapsed.set(rightCollapsed);

    return { leftPaneWidthPx, leftCollapsed, rightCollapsed };
  }

  /**
   * Updates pane opacity based on current widths and collapsed states
   */
  private updatePaneOpacity(leftPaneWidthPx: number, containerWidthPx: number, leftCollapsed: boolean, rightCollapsed: boolean): void {
    // Left pane opacity
    if (leftCollapsed) {
      this.leftPaneOpacitySignal.set(1);
    } else if (leftPaneWidthPx < MIN_LEFT_PANE_WIDTH_PX) {
      const opacity = (leftPaneWidthPx - COLLAPSED_LEFT_PANE_WIDTH_PX) / MIN_LEFT_PANE_WIDTH_PX;
      this.leftPaneOpacitySignal.set(opacity);
    } else {
      this.leftPaneOpacitySignal.set(1);
    }

    // Right pane opacity
    if (rightCollapsed) {
      this.rightPaneOpacitySignal.set(1);
    } else {
      const rightPaneWidthPx = containerWidthPx - leftPaneWidthPx - HANDLE_WIDTH_PX;
      if (rightPaneWidthPx < MIN_RIGHT_PANE_WIDTH_PX) {
        const opacity = (rightPaneWidthPx - COLLAPSED_RIGHT_PANE_WIDTH_PX) / MIN_RIGHT_PANE_WIDTH_PX;
        this.rightPaneOpacitySignal.set(opacity);
      } else {
        this.rightPaneOpacitySignal.set(1);
      }
    }
  }
}
